define([
    'underscore',
    'moment',
], (_, moment) => {

    return ['LocaleService', 'InsuranceSuiteMetadata', function (LocaleService, InsuranceSuiteMetadata) {

        return {
            formatDate: formatDate,
            formatLocalDate: formatLocalDate,
            formatNumber: formatNumber,
            sortByFields: sortByFields,
            isEnglishman: isEnglishman,
            isEdgeLanguage: isEdgeLanguage,

            /**
             * @returns document languages
             * lang.de, lang.fr, lang.it (no lang.en)
             */
            getDocumentLanguages: function () {
                const languageTypeMeta = InsuranceSuiteMetadata.get('pc').types.getTypelist('LanguageType');
                const availableLocales = LocaleService.getLocaleInfo().locales;
                const mappedLanguages = _.filter(
languageTypeMeta.codes,
                    (item) => {
                        return !_.isUndefined(availableLocales[item.code]) && !isEnglishman(item.code) && !isEdgeLanguage(item.code);
                    }
);
                return mappedLanguages;
            },

            /**
             *
             * @returns sets default document language (according UI language),
             * however if language is already non-Enligsh, it is kept as is
             */
            setOrResetDocumentLanguage: function (currentValue) {
                if (currentValue && !isEnglishman(currentValue.code)) {
                    //do not reset existing value
                    return currentValue;
                }
                if (!isEnglishman(LocaleService.getCurrentLocale())) {
                    return LocaleService.getCurrentLocale();
                }
                    return null;

            },

            /**
             * Context dependant date validation (ignoring time):
             *      periodStartDateInFutureContext=true, then date must be: [now ... future]
             *      periodStartDateInFutureContext!=true, then date must be: [now-14 ... future]
             * @param periodStartDate submission.draftData.periodStartDate
             * @param periodStartDateInFutureContext context of validation (RiskData page -> true, other pages -> false)
             * @return true | false (see description)
             */
            isNotExpiredQuote: (periodStartDate, periodStartDateInFutureContext) => {
                const today = moment(new Date());
                const lastPossibleDate = moment(new Date()).add('day', -14);
                const periodStartDateMoment = moment(periodStartDate);

                return (periodStartDateInFutureContext === true && (periodStartDateMoment.isAfter(today, 'day') || periodStartDateMoment.isSame(today, 'day')) || //NOSONAR
                periodStartDateInFutureContext !== true && (periodStartDateMoment.isAfter(lastPossibleDate, 'day') || periodStartDateMoment.isSame(lastPossibleDate, 'day'))); //NOSONAR
            },


            /**
             * Period Start Date cannot be later than one year in the future
             * @param periodStartDate submission.draftData.periodStartDate
             * @return true | false (see description)
             */
            validatePeriodStartDateNotTooFarInTheFuture: (periodStartDate) => {
                const oneYearInTheFuture = moment(new Date()).add('year', 1);
                const periodStartDateMoment = moment(periodStartDate);

                return periodStartDateMoment.isBefore(oneYearInTheFuture, 'day') || periodStartDateMoment.isSame(oneYearInTheFuture, 'day'); //NOSONAR
            },

            /**
             * Returns for a given codevalue the appropriate code object
             * @param typelistName The name of the typelist where the code belongs to
             * @param codeValue The String representation of the Code.
             */
            getTypecode(typelistName, codeValue) {
                const typelist = InsuranceSuiteMetadata.get('pc').types.getTypelist(typelistName);
                return _.findWhere(typelist.codes, {code: codeValue});
            },
        };
    }];

    /**
     * Function to format a date iso string to our common date format.
     * Example:
     *  input date iso string: 1980-12-27T23:00:00Z
     *  output: 28.12.1980
     * @param isoDateString
     * @returns formatted String dd.mm.yyyy
     */
    function formatDate(isoDateString) {
        const date = new Date(isoDateString);
        const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
        const month = (date.getMonth() + 1) < 10 ? `0${date.getMonth() + 1}` : (date.getMonth() + 1);
        const year = date.getFullYear();
        const dateString = `${day}.${month}.${year}`;
        return dateString;
    }

    /**
     * Function to format a local date to our common date format.
     * Example:
     *  input date iso string: {'year': 2016, 'month': 11. 'day': 25}
     *  output: 25.12.2016
     * @param isoDateString
     * @returns formatted String dd.mm.yyyy
     */
    function formatLocalDate(localDate) {
        const day = localDate.day < 10 ? `0${localDate.day}` : localDate.day;
        const month = (localDate.month + 1) < 10 ? `0${localDate.month + 1}` : (localDate.month + 1);
        const year = localDate.year;
        const dateString = `${day}.${month}.${year}`;
        return dateString;
    }

    /**
     * Function to format numbers (grouping)
     * Example:
     *  input:  1000000
     *  output: 1'000'000
     * @param number
     */
    function formatNumber(number) {
        let nr = `${number}`;
        const regexp = /(\d+)(\d{3})/;
        const pattern = new RegExp(regexp);
        while (pattern.test(nr)) {
            const match = regexp.exec(nr);
            nr = nr.replace(regexp, `${match[1]}'${match[2]}`);
        }
        return nr;
    }

    /**
     * Returns an array that contains the elements of arry, but sorted by given sortFields in that order.
     * Example: sortByFields(vehicles, ['make', 'type', 'price']) will sort the given
     * array first by make, then by type and finally by price.
     *
     * Does NOT work in place.
     * @param arry
     * @param sortFields
     * @returns {*}
     */
    function sortByFields(arry, sortFields) {
        // don't return the input array
        if (arry.length === 0) {
            return [];
        }
        if (!_.isArray(sortFields) || sortFields.length === 0) {
            throw new Error('sortFields must be an array with >= 1 elements');
        }


        // Sorting Approach: _.sortyBy() is stable, so sort the input array once per sortField, in reverse order.
        // we do this by using a right fold with the _.sortBy() function as a combinator.
        const reduceFn = function (currArray, field) {
            return _.sortBy(currArray, field);
        };
        return _.reduceRight(sortFields, reduceFn, arry);
    }

    /**
     * Returns true if given locale is any English (UK, US, etc)
     */
    function isEnglishman(locale) {
        return locale && locale.indexOf('en') === 0;
    }

    /**
     * Checks if the given locale string contains the "_EDG" substring.
     */
    function isEdgeLanguage(locale) {
        return typeof locale === 'string' && locale.indexOf('_EDG') >= 0;
    }
});



// WEBPACK FOOTER //
// ../common/js/src/edge/quoteandbind/baslercommon/services/BaslerCommonService.js